import { Component, Vue, Prop } from 'vue-property-decorator';
import RequestButtonDataTable from '@/components/request/RequestButtonDataTable/RequestButtonDataTable.vue';
import RequestButtonKanbanBoard from '@/components/request/RequestButtonKanbanBoard/RequestButtonKanbanBoard.vue';
import RequestButtonCreate from '@/components/request/RequestButtonCreate/RequestButtonCreate.vue';
import RequestDataTable from '@/components/request/RequestDataTable/RequestDataTable';
import RequestKanbanBoard from '@/components/request/RequestKanbanBoard/RequestKanbanBoard';
import { User } from '@/models/User';
import { Options } from '@/components/mi-dialog/MiDialog';
import { BoxRequest } from '@/models/BoxRequest';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';

@Component<RequestsTab>({
  components: {
    'request-button-data-table': RequestButtonDataTable,
    'request-button-kanban-board': RequestButtonKanbanBoard,
    'request-button-create': RequestButtonCreate,
    'request-data-table': RequestDataTable,
    'request-kanban-board': RequestKanbanBoard,
  }
})
export default class RequestsTab extends Vue {
  @Prop()
  private user!: User;

  private viewType: RequestViewType = 'data-table';

  private inProgress: BoxRequest | null = null;

  private handleCreateClick(): void {
    this.$store.dispatch('openDialog', this.createConfirmDialog);
  }

  private get createBoxRequestInProgressDialog(): Options {
    return {
      title: 'Bestaande order gevonden',
      text: `Er is al een order voor deze klant`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Toon order',
          action: (): void => {
            if (this.inProgress) {
              window.location.href = `/aanvragen/${this.inProgress.id}`;
            }
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  private async createRequest(): Promise<void> {
    if (! this.user.id) { return; }

    this.inProgress = null;
    const inProgress: BoxRequest | null = await new BoxRequest()
      .getInProgress(this.user.id);

    if (inProgress && inProgress.id) {
      this.inProgress = inProgress;
      this.$store.dispatch('openDialog', this.createBoxRequestInProgressDialog);

      return;
    }

    const requestCreatePayload: RequestCreatePayload = {
      user: this.user.id,
    };
    const request: BoxRequest = await new BoxRequest()
      .create(requestCreatePayload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    if (! request) {
      this.$store.dispatch('openDialog', this.createFailedDialog);
      return;
    }
    this.$router.push(`/aanvragen/${request.id}`);
  }

  private get showDataTable(): boolean {
    return this.viewType === 'data-table';
  }

  private get showKanbanBoard(): boolean {
    return this.viewType === 'kanban';
  }

  private get createConfirmDialog(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: `Weet je zeker dat je een nieuwe aanvraag wil aanmaken voor ${this.user.name}?`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Ja, aanvraag aanmaken',
          action: (): void => {
            this.createRequest();
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  private get createFailedDialog(): Options {
    return {
      title: 'Aanmaak nieuwe aanvraag mislukt',
      text: 'Het aanmaken van een nieuwe aanvraag is mislukt',
      type: 'error',
      buttons: {
        confirm: {
          text: 'Oké',
        },
      }
    };
  }
}

export type RequestViewType = 'data-table' | 'kanban';

export type RequestCreatePayload = {
  user?: string;
};
